import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  FiArrowLeft,
  FiFileText,
  FiMapPin,
  FiUser,
  FiHome,
  FiDollarSign,
} from "react-icons/fi";
import PropertyComment from "../property/PropertyComment";
import { showToast } from "../../../components/common/notification/Notification";
import styles from "./PropertyDiscussionPage.module.css";

const PropertyDiscussionPage = ({ back = "/customer/properties" }) => {
  const { Id } = useParams();
  const navigate = useNavigate();

  const [propertyData, setPropertyData] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [loadingProperty, setLoadingProperty] = useState(false);

  const loggedInUser = useSelector((state) => state.auth?.user);
  const token =
    loggedInUser?.accessToken ||
    loggedInUser?.token ||
    loggedInUser?.refreshToken;
  const baseUrl = process.env.REACT_APP_BASE_URL;

  useEffect(() => {
    if (Id) {
      fetchPropertySummary();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Id]);

  const fetchPropertySummary = async () => {
    try {
      setLoadingProperty(true);
      const response = await axios.get(
        `${baseUrl}/property/propertyDetail/${Id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        const info = response.data.propertyInfo || response.data;
        const owner = info.user?.id || info.user_id;

        const rawFirstName = info.user?.first_name;
        const rawLastName = info.user?.last_name;
        let cleanName = [rawFirstName, rawLastName].filter(Boolean).join(" ");
        if (!cleanName || cleanName.toLowerCase().includes("null")) {
          cleanName = info.user?.name || "Property Owner";
        }

        setPropertyData({
          propertyId: info.id || Id,
          ownerId: owner,
          title: info.name || info.title || "Property Details",
          creatorName: cleanName,
          type: info.property_sub_type?.property_type_name || info.property_type?.name,
          city: info.city_name,
          state: info.state_name,
          price: info.price,
        });
      }
    } catch (err) {
      console.error("Failed to load property summary for discussion:", err);
      showToast("Error loading property information", { type: "error" });
    } finally {
      setLoadingProperty(false);
    }
  };

  const normalizedBack = back.replace(/\/+$/, "");
  const detailsPath = `${normalizedBack}/${Id}`;

  const formattedPrice =
    propertyData?.price !== undefined &&
    propertyData?.price !== null &&
    propertyData?.price !== ""
      ? isNaN(Number(propertyData.price))
        ? propertyData.price
        : `₹ ${Number(propertyData.price).toLocaleString("en-IN")}`
      : null;

  return (
    <div className={styles.wrapper}>
      {/* 1. Page Header */}
      <div className={styles.pageHeader}>
        <div className={styles.pageHeaderLeft}>
          <button
            type="button"
            className={styles.headerBackBtn}
            onClick={() => navigate(detailsPath)}
            aria-label="Back to property details"
            title="Back to property details"
          >
            <FiArrowLeft size={16} />
            <span>Property Details</span>
          </button>
          <div className={styles.headerTitles}>
            <span className={styles.pageCategory}>Community Discussion</span>
            <h1 className={styles.pageTitle}>Property Comments & Inquiries</h1>
          </div>
        </div>
      </div>

      {/* 2. Compact Property Summary Banner */}
      <div className={styles.summaryCard}>
        <div className={styles.summaryLeft}>
          <div className={styles.summaryIconBox}>
            <FiHome />
          </div>
          <div>
            <h3 className={styles.summaryTitle}>
              {propertyData?.title || `Property Listing #${Id}`}
            </h3>
            <div className={styles.summaryMeta}>
              {propertyData?.creatorName && (
                <span className={styles.metaItem}>
                  <FiUser className="text-muted" />
                  <span>Listed by {propertyData.creatorName}</span>
                </span>
              )}
              {propertyData?.type && (
                <span className={styles.metaItem}>
                  <span className="badge bg-light text-primary border border-primary-subtle">
                    {propertyData.type}
                  </span>
                </span>
              )}
              {(propertyData?.city || propertyData?.state) && (
                <span className={styles.metaItem}>
                  <FiMapPin className="text-danger" />
                  <span>
                    {[propertyData.city, propertyData.state]
                      .filter(Boolean)
                      .join(", ")}
                  </span>
                </span>
              )}
              {formattedPrice && (
                <span className={`${styles.metaItem} ${styles.priceTag}`}>
                  <FiDollarSign className="text-success" />
                  <span>{formattedPrice}</span>
                </span>
              )}
            </div>
          </div>
        </div>

        <div className={styles.summaryRight}>
          <button
            type="button"
            className={styles.summaryDetailsBtn}
            onClick={() => navigate(detailsPath)}
          >
            <FiFileText size={14} />
            <span>View Full Details</span>
          </button>
        </div>
      </div>

      {/* 3. Discussion & Comment Thread */}
      <div className={styles.discussionMain}>
        <PropertyComment
          Id={Id}
          ownerId={propertyData?.ownerId}
          commentType="propertyComments"
          allComments="/property/requestPropertyComments"
          addComment="/property/addRequestPropertyComment"
          editComment="/property/editRequestPropertyComment"
          deleteComment="/property/deleteRequestPropertyComment/"
        />
      </div>
    </div>
  );
};

export default PropertyDiscussionPage;
